import type { DomainKey } from '@/lib/domain-config';

/** A brand's profile on a social network. */
export interface SocialProfile {
  label: string;
  url: string;
  /**
   * Footer icon. A profile without one is published only as JSON-LD `sameAs`
   * — LinkedIn, which the footer does not show (SH-468).
   */
  icon?: string;
}

export interface OfferedService {
  name: string;
  description: string;
}

/**
 * A `Service` in the homepage's public offer catalogue. Unlike the Standort
 * catalogue's entries it carries the page it describes, because the homepage
 * node names three whole product areas rather than five subjects taught at one
 * Standort (SH-530, 10.03.03.34-JsonLD-Homepage § AC 3).
 */
export interface LinkedService extends OfferedService {
  /** Site-relative, e.g. `/online-nachhilfe/`. Made absolute by the builder. */
  path: string;
}

/**
 * The publisher facts the homepage's `Organization` node needs
 * (10.03.03.34-JsonLD-Homepage). Brand-level and rarely changing, so they live
 * here with the rest of the brand chrome rather than in the CMS.
 */
export interface BrandOrganization {
  legalName: string;
  /** Path under `public/`; the builder makes it absolute on the canonical origin. */
  logoPath: string;
  /**
   * The company hotline. **Deliberately not routed through the 70.07 phone
   * chain**: that chain resolves a page-specific pair (a Matelso display label
   * for call tracking vs the dialable number), and this node is a statement
   * about the company rather than about the page it appears on — publishing a
   * tracking number as the public contact point would be wrong. Likewise not
   * read from `GET /domain-settings`, a conscious deviation from the
   * per-brand-values rule for the same reason.
   */
  hotline: string;
  /** `contactPoint.areaServed`, ISO 3166-1 alpha-2. */
  areaServed: string;
  /** `contactPoint.availableLanguage`. */
  availableLanguage: string;
  /** The homepage's own catalogue — *not* the Standort `offerCatalog` above. */
  offerCatalog: { name: string; services: LinkedService[] };
}

/**
 * Fixed, brand-level facts the website publishes: the footer's social icons
 * and a Standort's `LocalBusiness` JSON-LD (`sameAs`, `hasOfferCatalog`).
 * One list feeds both so the footer and the structured data cannot drift
 * apart again (SH-468).
 */
export interface BrandProfile {
  socialProfiles: SocialProfile[];
  offerCatalog: { name: string; services: OfferedService[] };
  /**
   * Homepage `Organization` node. Optional: a brand without it publishes the
   * `WebSite` node alone, which is what every brand did before SH-530. Only
   * `de` is in scope today — the other three differ in legal entity, language
   * and catalogue copy, and gain an entry when those values are decided.
   */
  organization?: BrandOrganization;
}

// Hard-coded like the rest of the footer chrome (10.03.01.02 § Business Logic).
// Only Schülerhilfe Deutschland is filled in: the other brands are out of scope
// for now and get their own entry here once their values are known. A brand
// without an entry publishes neither `sameAs` nor `hasOfferCatalog`.
// The footer shows the profiles in this order; `sameAs` order carries no
// meaning in schema.org.
export const BRAND_PROFILES: Partial<Record<DomainKey, BrandProfile>> = {
  de: {
    socialProfiles: [
      {
        label: 'Facebook',
        url: 'https://www.facebook.com/Schuelerhilfe/',
        icon: '/icons/svg/social-facebook.svg',
      },
      {
        label: 'YouTube',
        url: 'https://www.youtube.com/user/schuelerhilfechannel',
        icon: '/icons/svg/social-youtube.svg',
      },
      {
        label: 'Instagram',
        url: 'https://www.instagram.com/schuelerhilfe_gmbh/',
        icon: '/icons/svg/social-instagram.svg',
      },
      {
        label: 'LinkedIn',
        url: 'https://www.linkedin.com/company/schuelerhilfe',
      },
    ],
    offerCatalog: {
      name: 'Nachhilfe-Leistungen',
      services: [
        {
          name: 'Online-Nachhilfe',
          description:
            'Interaktive Online-Nachhilfe in allen gängigen Schulfächern via Lernplattform.',
        },
        {
          name: 'Nachhilfe (Allgemein)',
          description:
            'Qualifizierter Nachhilfeunterricht für alle Fächer, Klassen und Schulformen.',
        },
        {
          name: 'Mathematik Nachhilfe',
          description:
            'Individuelle Mathe-Förderung für alle Klassenstufen vor Ort.',
        },
        {
          name: 'Deutsch Nachhilfe',
          description:
            'Gezieltes Training in Rechtschreibung, Grammatik und Textanalyse.',
        },
        {
          name: 'Englisch Nachhilfe',
          description:
            'Verbesserung von Wortschatz, Grammatik und sicherem Sprechen.',
        },
      ],
    },
    organization: {
      legalName: 'ZGS Bildungs-GmbH',
      // The site's own logo. The legacy node points at a `/typo3conf/ext/…`
      // path that does not survive the relaunch.
      logoPath: '/schuelerhilfe-logo.svg',
      hotline: '+49 800 3020090 87',
      areaServed: 'DE',
      availableLanguage: 'German',
      // Three product areas, each linking to the page that describes it — not
      // the five subject entries of the Standort `offerCatalog` above. Two
      // different catalogues about two different subjects; both are published.
      offerCatalog: {
        name: 'Schülerhilfe Bildungsangebote',
        services: [
          {
            name: 'Nachhilfe vor Ort',
            description:
              'Professionelle Nachhilfe als Einzelnachhilfe oder in kleinen Gruppen für alle Klassen und Fächer an über 1.100 Standorten.',
            path: '/',
          },
          {
            name: 'Online-Nachhilfe',
            description:
              'Flexible Online-Einzelnachhilfe oder in der kleinen Gruppe mit professionellen Tutoren, Zugang zum Online-LernCenter und interaktiven Lernmaterialien.',
            path: '/online-nachhilfe/',
          },
          {
            name: 'Ferienkurse & Prüfungsvorbereitung',
            description:
              'Gezielte Vorbereitung auf Klausuren, Mittlere Reife und das Abitur.',
            path: '/service/haeufig-gestellte-fragen/',
          },
        ],
      },
    },
  },
};
